import {useCallback} from 'react';
import {type EnrichedAstNode} from 'rendition-core';
import {RenderFrameDiv} from 'Components/RenderFrameDiv';
import {NodeLocationInfo} from 'Contexts/NodeCallBackContext';

interface Props {
    scale: React.MutableRefObject<number>;
    node: EnrichedAstNode;
    locationCallBack: () => NodeLocationInfo;
    horizontalOnly?: boolean;
    verticalOnly?: boolean;
}

export const MarginHighlighter: React.FC<React.PropsWithChildren<Props>> = ({
    node,
    locationCallBack,
    scale,
    horizontalOnly,
    verticalOnly,
}) => {
    const topMargin = node.position.marginTop;
    const bottomMargin = node.position.marginBottom;
    const leftMargin = node.position.marginLeft;
    const rightMargin = node.position.marginRight;

    const top = useCallback(() => {
        const location = locationCallBack();
        return {
            width: location.actual.width + 'px',
            height: topMargin * scale.current + 'px',
            top: topMargin * scale.current * -1 + 'px',
        };
    }, [topMargin, scale, locationCallBack]);

    const bottom = useCallback(() => {
        const location = locationCallBack();
        return {
            width: location.actual.width + 'px',
            height: bottomMargin * scale.current + 'px',
            top: location.actual.height + 'px',
        };
    }, [bottomMargin, scale, locationCallBack]);

    const left = useCallback(() => {
        const location = locationCallBack();
        return {
            width: leftMargin * scale.current + 'px',
            height: location.actual.height + topMargin * scale.current + bottomMargin * scale.current + 'px',
            left: leftMargin * scale.current * -1 + 'px',
            top: topMargin * scale.current * -1 + 'px',
        };
    }, [leftMargin, scale, locationCallBack, bottomMargin, topMargin]);

    const right = useCallback(() => {
        const location = locationCallBack();
        return {
            width: rightMargin * scale.current + 'px',
            height: location.actual.height + topMargin * scale.current + bottomMargin * scale.current + 'px',
            left: location.actual.width + 'px',
            top: topMargin * scale.current * -1 + 'px',
        };
    }, [rightMargin, scale, locationCallBack, bottomMargin, topMargin]);

    return (
        <>
            {!horizontalOnly && (
                <>
                    <RenderFrameDiv
                        onRenderFrame={top}
                        className="absolute bg-yellow-300 opacity-40"
                        style={{
                            left: 0,
                        }}
                    />
                    <RenderFrameDiv
                        onRenderFrame={bottom}
                        className="absolute bg-yellow-300 opacity-40"
                        style={{
                            left: 0,
                        }}
                    />
                </>
            )}
            {!verticalOnly && (
                <>
                    <RenderFrameDiv
                        onRenderFrame={left}
                        className="absolute bg-yellow-300 opacity-40"
                        style={{
                            top: 0,
                        }}
                    />
                    <RenderFrameDiv
                        onRenderFrame={right}
                        className="absolute bg-yellow-300 opacity-40"
                        style={{
                            top: 0,
                        }}
                    />
                </>
            )}
        </>
    );
};
